import { Icon } from '@eventbrite/eds-icon';
import { ChevronDownChunky } from '@eventbrite/eds-iconography';
import { gettext } from '@eventbrite/i18n';
import React from 'react';
import { connect } from 'react-redux';
import { ALL_TAB, FEED_TABS, FOR_YOU } from '../../../../constants/tabConfig';
import {
    FeatureFlags,
    SeasonalContent,
    SeasonalContents,
} from '../../../../types/index';
import { StickyTabs } from '../../components/Tabs';
import { fetchEventsForTab, fetchUserSavedEvents } from '../../redux/events/';
import { injectSeasonalTab, scrollIt } from '../../utils';
import { FeedBuilderContainer } from '../FeedBuilderContainer';
import { HomeLocationAutocompleteContainer } from '../HomeLocationAutocompleteContainer';
import './BrowseContainer.scss';
import { ForYouTabKey } from './ForYouTabKey';

const Chevron = (
    <Icon type={<ChevronDownChunky />} color="ui-blue--hover" size="medium" />
);

export const ONLINE_EVENTS_INPUT_COPY = gettext('Popular ');

const LOCATION_AUTOCOMPLETE_PROPS = {
    prefix: Chevron,
    borderType: 'content',
    id: 'locationPicker',
    name: 'locationPicker',
    hideOutline: true,
    isLimitedSearch: true,
    preventInputValuePrefill: true,
    shouldRemoveConflictiveAria: true,
};

const shouldIncludeCurrentTab = ({
    tabKey,
    tabsConfig,
}: {
    tabKey?: string;
    tabsConfig: { [key: string]: any };
}) => {
    if (!tabKey || !tabsConfig[tabKey]) {
        return false;
    }

    return true;
};

const getBrowseTabs = (seasonalContent?: SeasonalContents) => {
    const tabsConfig = injectSeasonalTab(seasonalContent);
    const tabs: {
        tabKey: string;
        value: number;
        displayName: string;
        content: JSX.Element;
    }[] = [];

    FEED_TABS.forEach((tabKey, index) => {
        const shouldAddTab = shouldIncludeCurrentTab({
            tabKey,
            tabsConfig,
        });

        if (shouldAddTab) {
            const { name, eventSearch, follow, feedConfig } =
                tabsConfig[tabKey];
            let displayName = name;

            if (tabKey === FOR_YOU) {
                displayName = <ForYouTabKey />;
            }

            tabs.push({
                tabKey,
                value: index,
                displayName: displayName,
                content: (
                    <FeedBuilderContainer
                        feedConfig={feedConfig}
                        eventSearch={eventSearch}
                        follow={follow}
                        tabKey={tabKey}
                    />
                ),
            });
        }
    });

    return tabs;
};

const LocationComponent = ({ location }: { location?: object }) => {
    const copy = gettext('Popular in ');

    return (
        <div className="browse__location eds-l-pad-top-4">
            <div className="browse__location-text">{copy} </div>
            <HomeLocationAutocompleteContainer
                autocompleteProps={LOCATION_AUTOCOMPLETE_PROPS}
                shouldDispatchSearchActions={true}
                location={location}
            />
        </div>
    );
};

const handleTabLoad = (
    tabKey: string,
    {
        fetchEventsForTab,
        isFeLoading,
    }: { fetchEventsForTab: Function; isFeLoading: boolean },
) => {
    /**
     * On first load we do not need to fetch new events ONLY for the all_tab.
     * The tab is loaded before the `fe` has finished fetching so we use that
     * variable to know if we are in the all tab to prevent fetching.
     */
    if ((!isFeLoading && tabKey === ALL_TAB) || tabKey !== ALL_TAB) {
        fetchEventsForTab({ tabKey, shouldUpdateContent: true });
    }
};

const handleTabChange = (
    tabKey: string,
    { fetchEventsForTab }: { fetchEventsForTab: Function },
) => {
    scrollIt(
        ((document.querySelector('.browse-section') as HTMLElement)
            ?.offsetTop || 0) + 70,
    );

    fetchEventsForTab({ tabKey, shouldUpdateContent: true });
};

interface BrowseProps {
    tabKey?: string;
    location?: object;
    seasonalContent?: SeasonalContents;
    isFeLoading: boolean;
    isNewHeader?: boolean;
    fetchEventsForTab: () => void;
    fetchUserSavedEvents: () => void;
}

const Browse = (props: BrowseProps) => {
    const {
        tabKey,
        location,
        seasonalContent,
        isFeLoading,
        isNewHeader,
        fetchEventsForTab,
        fetchUserSavedEvents,
    } = props;
    const items = getBrowseTabs(seasonalContent);
    const hasFetchedEventsRef = React.useRef(false);

    React.useEffect(() => {
        if (!hasFetchedEventsRef.current && tabKey === FOR_YOU) {
            hasFetchedEventsRef.current = true;

            window?.requestIdleCallback
                ? window.requestIdleCallback((_deadline) =>
                      fetchUserSavedEvents(),
                  )
                : fetchUserSavedEvents();
        }
    }, [fetchUserSavedEvents, tabKey]);

    return (
        <div className="browse-section" id="browse-section">
            <LocationComponent location={location} />

            <StickyTabs
                items={items}
                isNewHeader={isNewHeader}
                onTabChange={(tabKey: string) =>
                    handleTabChange(tabKey, {
                        fetchEventsForTab,
                    })
                }
                onTabLoad={(tabKey: string) =>
                    handleTabLoad(tabKey, {
                        fetchEventsForTab,
                        isFeLoading,
                    })
                }
                initialSelectedTab={tabKey}
                location={location}
            />
        </div>
    );
};

const _mapStateToProps = (state: {
    user: {
        isAuthenticated?: boolean;
    };
    content: {
        browseState?: {
            tabKey?: string;
        };
        seasonal: SeasonalContent;
    };
    app: {
        featureFlags?: FeatureFlags;
        loadingState?: {
            isFeLoading?: boolean;
        };
    };
    location: string;
}) => ({
    isAuthenticated: state.user.isAuthenticated,
    tabKey: state.content.browseState?.tabKey,
    isFeLoading: state.app.loadingState?.isFeLoading,
    location: state.location,
    seasonalContent: state.content.seasonal,
    isNewHeader: state.app.featureFlags?.launchHeader2021,
});

const _mapDispatchToProps = {
    fetchEventsForTab,
    fetchUserSavedEvents,
};

export default connect(_mapStateToProps, _mapDispatchToProps)(Browse);
